import type { UpdateCallOffPayload, UpdateCallOffResponse } from './types';

import { ENDPOINT } from '~/constants/endpoints';

import type { UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import {
  zodUpdateCallOffPayloadSchema,
  zodUpdateCallOffResponseSchema,
} from './validations';

/**
 * Updates an existing call-off's fields.
 * Status transitions are validated by the API.
 *
 * @param callOffId - The ID of the call-off to update.
 * @param payload - The call-off fields to update (partial update).
 * @returns The updated call-off.
 * @throws Error if the API request fails or validation fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Call-Offs/operation/update_calloff_calloffs__calloff_id__put
 */
export const updateCallOff = async (
  callOffId: UUID,
  payload: UpdateCallOffPayload,
) => {
  const response = await vestigasApi
    .put(ENDPOINT.CALL_OFF.UPDATE(callOffId), {
      json: payload,
      validate: {
        body: zodUpdateCallOffPayloadSchema,
      },
    })
    .json<UpdateCallOffResponse>();

  // Validate the response with Zod
  const validatedResponse = zodUpdateCallOffResponseSchema.safeParse(response);

  if (validatedResponse?.error?.issues?.length) {
    log.error({
      message: 'Update CallOff API response has validation errors',
      error: {
        errors: validatedResponse?.error?.issues,
        errorMessage: validatedResponse?.error?.message,
        response,
      },
    });
  }

  return response;
};
